/**
 * The Thesis Acknowledgements Generator: the people a thesis owes, thanked in the
 * conventional order — academic debts first (supervisors, then committee or examiners),
 * then funding and institutional support, then the participants and colleagues who gave
 * their time, then family and friends — in a formal or a warm register, three ways.
 *
 * The bank is spread into the widget's strings; frame syntax is documented in
 * `compose.ts`.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  asFragment,
  compose,
  endsInAppositive,
  joinList,
  orPlaceholder,
  splitItems,
  strong,
} from "@/lib/tool-kit/correspondence/compose";

export type AcknowledgementsTone = "formal" | "warm";
export type DocumentWord = "thesis" | "dissertation";
export type HeadingSpelling = "uk" | "us";
export type SupervisorTerm = "supervisor" | "advisor";
export type CommitteeTerm = "committee" | "examiners" | "panel";
export type DedicationStyle = "to" | "for" | "memory";

export const ACKNOWLEDGEMENTS_BANK = {
  headings: {
    uk: "Acknowledgements",
    us: "Acknowledgments",
  },
  documentWords: {
    thesis: "thesis",
    dissertation: "dissertation",
  },
  supervisorNouns: {
    supervisor: ["supervisor", "supervisors"],
    advisor: ["advisor", "advisors"],
  },
  committeeNouns: {
    committee: "the members of my committee",
    examiners: "my examiners",
    panel: "the members of my advisory panel",
  },
  defaultThanks: {
    supervisorFormal:
      "their guidance, patience, and support throughout this project",
    supervisorWarm:
      "their patience, their encouragement, and every careful read of my drafts",
    committeeFormal: "their insightful comments and questions",
    committeeWarm: "their generous and thoughtful feedback",
  },
  dedications: {
    to: "To {who}",
    for: "For {who}",
    memory: "In memory of {who}",
  },
  supervisorPlaceholder: "Supervisor's name",
  formal: [
    "?v1:I would like to express my sincere gratitude to my {supNoun}, {supervisors}, for {supThanks}.",
    "?v2:I am deeply grateful to my {supNoun}, {supervisors}, for {supThanks}.",
    "?v3:This {docWord} owes a great deal to my {supNoun}, {supervisors}, and I thank them for {supThanks}.",
    "¶",
    "?committee?v1:I also thank {committeeNoun}, {committee}, for {committeeThanks}.",
    "?committee?v2:I am grateful to {committeeNoun}, {committee}, for {committeeThanks}.",
    "?committee?v3:My thanks also go to {committeeNoun}, {committee}, for {committeeThanks}.",
    "¶",
    "?funders?v1:This research was supported by {funders}.",
    "?funders?v2:I gratefully acknowledge the financial support of {funders}.",
    "?funders?v3:This work would not have been possible without funding from {funders}.",
    "¶",
    "?institutions?v1:I am grateful to {institutions} for providing the facilities and support that made this work possible.",
    "?institutions?v2:I thank {institutions} for practical support throughout my research.",
    "?institutions?v3:I could not have carried out this work without the support of {institutions}.",
    "¶",
    "?participants?v1:I am deeply grateful to {participants}, without whom this research would not have been possible.",
    "?participants?v2:For their time and their trust, my sincere thanks go to {participants}.",
    "?participants?v3:This {docWord} rests on the generosity of {participants}, and I thank them for their time.",
    "¶",
    "?colleagues?v1:I would also like to thank {colleagues} for their advice and collegiality.",
    "?colleagues?v2:I have benefited greatly from the advice and friendship of {colleagues}.",
    "?colleagues?v3:I thank {colleagues} for many helpful discussions and for making the work more enjoyable.",
    "¶",
    "?family?v1:Finally, I thank {family} for their unwavering support and encouragement.",
    "?family?v2:Finally, I am grateful to {family}, whose support made this {docWord} possible.",
    "?family?v3:Last but not least, I thank {family} for their patience and encouragement throughout.",
  ],
  warm: [
    "?v1:First and foremost, a huge thank-you to my {supNoun}, {supervisors}, for {supThanks}.",
    "?v2:I owe an enormous debt to my {supNoun}, {supervisors}, for {supThanks}.",
    "?v3:My deepest thanks go to my {supNoun}, {supervisors}, for {supThanks}; I have learned more than I can say.",
    "¶",
    "?committee?v1:Thank you also to {committeeNoun}, {committee}, for {committeeThanks}.",
    "?committee?v2:I am very grateful to {committeeNoun}, {committee}, for {committeeThanks}.",
    "?committee?v3:Many thanks to {committeeNoun}, {committee}, for {committeeThanks}.",
    "¶",
    "?funders?v1:This research was made possible by funding from {funders}, and I am very grateful for it.",
    "?funders?v2:I am grateful to {funders} for funding this research.",
    "?funders?v3:Thank you to {funders} for supporting this work.",
    "¶",
    "?institutions?v1:Thank you to {institutions} for giving me a place to do this work and the support to do it well.",
    "?institutions?v2:I am grateful to everyone at {institutions} for their help and kindness.",
    "?institutions?v3:I could not have done this work without the support of {institutions}.",
    "¶",
    "?participants?v1:A special thank-you to {participants}: this research would not exist without you.",
    "?participants?v2:For their time, trust, and openness, I am deeply grateful to {participants}.",
    "?participants?v3:To {participants}: thank you for your time and trust.",
    "¶",
    "?colleagues?v1:Thank you to {colleagues} for the advice, the laughs, and the company.",
    "?colleagues?v2:I was lucky to work alongside {colleagues}, who made every stage of this project better.",
    "?colleagues?v3:To {colleagues}: thank you for the conversations, the encouragement, and the company.",
    "¶",
    "?family?v1:Finally, to {family}: thank you for everything. I could not have done this without you.",
    "?family?v2:Above all, I thank {family}, who believed in me even when I did not.",
    "?family?v3:Last, and most of all, thank you to {family} for your love, patience, and support.",
  ],
} as const;

export interface AcknowledgementsBank {
  headings: Readonly<Record<HeadingSpelling, string>>;
  documentWords: Readonly<Record<DocumentWord, string>>;
  supervisorNouns: Readonly<Record<SupervisorTerm, readonly string[]>>;
  committeeNouns: Readonly<Record<CommitteeTerm, string>>;
  defaultThanks: Readonly<
    Record<
      | "supervisorFormal"
      | "supervisorWarm"
      | "committeeFormal"
      | "committeeWarm",
      string
    >
  >;
  dedications: Readonly<Record<DedicationStyle, string>>;
  supervisorPlaceholder: string;
  formal: readonly string[];
  warm: readonly string[];
}

export interface AcknowledgementsInput {
  documentWord: DocumentWord;
  spelling: HeadingSpelling;
  tone: AcknowledgementsTone;
  phrasing: "v1" | "v2" | "v3";
  supervisorTerm: SupervisorTerm;
  /** One name per line, as the writer wants it printed ("Professor Ana Lima"). */
  supervisors: string;
  /** Finishes "…for": "their patience and…". Blank uses a conventional phrase. */
  supervisorThanks: string;
  committeeTerm: CommitteeTerm;
  committee: string;
  committeeThanks: string;
  funders: string;
  institutions: string;
  /** Who took part, described — never named. */
  participants: string;
  colleagues: string;
  family: string;
  dedication: string;
  dedicationStyle: DedicationStyle;
}

export interface AcknowledgementsDraft {
  /** The dedication line, or "" — it belongs on its own page. */
  dedication: string;
  /** The heading (marked strong), then one paragraph per group thanked. */
  paragraphs: string[];
}

/** A "…for" phrase without a doubled "for": "for their patience" → "their patience". */
const LEADING_FOR = /^for\s+/i;

function thanksPhrase(raw: string, fallback: string): string {
  const phrase = asFragment(raw).replace(LEADING_FOR, "");
  return phrase === "" ? fallback : phrase;
}

export function buildAcknowledgements(
  input: AcknowledgementsInput,
  bank: AcknowledgementsBank,
  locale: string
): AcknowledgementsDraft {
  const values: Record<string, string> = {};
  const flags = new Set<string>([input.phrasing]);
  const warm = input.tone === "warm";

  const supervisors = splitItems(input.supervisors);
  const nouns = bank.supervisorNouns[input.supervisorTerm];
  values.supNoun = (supervisors.length > 1 ? nouns[1] : nouns[0]) ?? "";
  values.supervisors = orPlaceholder(
    joinList(supervisors, locale),
    bank.supervisorPlaceholder
  );
  values.supThanks = thanksPhrase(
    input.supervisorThanks,
    warm
      ? bank.defaultThanks.supervisorWarm
      : bank.defaultThanks.supervisorFormal
  );
  values.committeeNoun = bank.committeeNouns[input.committeeTerm];
  values.committeeThanks = thanksPhrase(
    input.committeeThanks,
    warm ? bank.defaultThanks.committeeWarm : bank.defaultThanks.committeeFormal
  );
  values.docWord = bank.documentWords[input.documentWord];

  const open = new Set<string>();
  if (endsInAppositive(supervisors)) {
    open.add("supervisors");
  }
  for (const key of [
    "committee",
    "funders",
    "institutions",
    "colleagues",
    "family",
  ] as const) {
    const items = splitItems(input[key]);
    if (items.length > 0) {
      values[key] = joinList(items, locale);
      flags.add(key);
    }
    if (endsInAppositive(items)) {
      open.add(key);
    }
  }
  const participants = asFragment(input.participants);
  if (participants !== "") {
    values.participants = participants;
    flags.add("participants");
  }
  if (participants.includes(",")) {
    open.add("participants");
  }

  const who = asFragment(input.dedication);
  return {
    dedication:
      who === "" ? "" : t(bank.dedications[input.dedicationStyle], { who }),
    paragraphs: [
      strong(bank.headings[input.spelling]),
      ...compose(warm ? bank.warm : bank.formal, values, flags, open),
    ],
  };
}
